import FileDownload from 'js-file-download';
import qs from 'qs';

import {IFieldMeta, IUrlConfig} from 'entities/metadata';
import {EEntityType} from 'shared/constants/entities';
import {ERequestType} from 'shared/types/requests';
import {
    convertToFormData,
    performRequest,
    TRequestPromise,
} from 'shared/utils';

import {
    TDataRequestTableParams,
    TDataResponseDirectory,
    TDataResponseTable,
    TEntity,
    TLookupValue,
    TTableEntityBodyParameters,
    EUserIdUrl,
} from './data-types';
import {convertParamsForRequest, getFilenameFromResponse} from './utils/data.utils';

export const fetchForCheckConfirm = (referenceUrl: string, params: Record<string, any>) => performRequest({
    url: `${referenceUrl}`,
    method: ERequestType.GET,
    params,
});
export const fetchFormData = (
    entityName: string,
    id: string,
    entityComplexName?: string,
    params?: any,
    urlConfig?: IUrlConfig,
) => {
    if (entityComplexName) {
        return performRequest({
            url: `${entityComplexName}/${id}`,
            method: ERequestType.GET,
            params,
            urlConfig,
        });
    }

    return performRequest({
        url: `${entityName}/${id}`,
        method: ERequestType.GET,
        urlConfig,
    });
};

export const fetchFormPageData = (
    entityName: string,
    entityComplexName?: string,
    params: TDataRequestTableParams = {},
    body?: TTableEntityBodyParameters,
    userId?: number,
    urlConfig?: IUrlConfig,
): TRequestPromise<TDataResponseTable> => {
    if (entityComplexName) {
        return performRequest({
            url: `${entityComplexName}`,
            method: ERequestType.GET,
            data: body,
            params,
            headers: {userId},
            urlConfig,
        });
    }

    return performRequest({
        url: `${entityName}/${EEntityType.FORM.toLowerCase()}`,
        method: ERequestType.GET,
        data: body,
        params,
        urlConfig,
    });
};

export interface IFetchReferenceDataParams {
  referenceUrl: string;
  referenceParams?: Record<string, any>;
  urlParam?: string;
  requestType?: ERequestType;
}

export const fetchReferenceData = <T = TEntity[]>(
    params: IFetchReferenceDataParams,
): TRequestPromise<T> => {
    const {
        referenceUrl,
        referenceParams,
        urlParam,
        requestType = ERequestType.GET,
    } = params ?? {};

    return performRequest({
        url: urlParam ? `${referenceUrl}/${urlParam}` : `${referenceUrl}`,
        method: requestType,
        params: referenceParams && convertParamsForRequest(referenceParams),
    });
};

export const fetchFormDataFields = (
    referenceUrl?: string,
    id?: string | number,
    urlParam?: string,
    referenceParams?: Record<string, any>,
): TRequestPromise<IFieldMeta[]> => performRequest({
    url: `${referenceUrl}/${urlParam || id}`,
    method: ERequestType.GET,
    params: referenceParams && convertParamsForRequest(referenceParams),
});
export const fetchPermissionDisplayFileComponent = (
    referenceUrl?: string,
    id?: string | number,
):TRequestPromise<boolean> => performRequest({
    url: `${referenceUrl}/${id}`,
    method: ERequestType.GET,
});

export const fetchTableData = (
    entityType: EEntityType,
    entityName: string,
    params: TDataRequestTableParams = {},
    entityComplexName?: string,
    userId?: number,
    prefix?: string,
    urlConfig?: IUrlConfig,
): TRequestPromise<TDataResponseTable> => {
    const headers = entityComplexName === EUserIdUrl.news
    || entityName === EUserIdUrl.news
    || entityName === EUserIdUrl.settingsUser
    || entityName === EUserIdUrl.rolesAndOrganizations
    || entityComplexName === EUserIdUrl.settingsUser ? {userId} : undefined;
    const url = prefix || entityComplexName || entityName;

    const entityReportData = {
        DOC_ID: -1,
        page: 0,
        size: 5,
    };

    return performRequest({
        url: `${url}`,
        method: entityName === 'tax.accounting.income.report' ? ERequestType.POST : ERequestType.GET,
        params,
        headers,
        paramsSerializer: entityName === 'tax.accounting.income.report'
            ? undefined
            : () => qs.stringify(params, {arrayFormat: 'repeat'}),
        data: entityName === 'tax.accounting.income.report' ? entityReportData : undefined,
        urlConfig,
    });
};

export const fetchDirectoryData = (
    entityName: string,
): TRequestPromise<TDataResponseDirectory> => performRequest({
    url: `${entityName}`,
    method: ERequestType.GET,
});

export const postDirectoryData = (
    referenceUrl: string,
    requestType: ERequestType,
    data: Record<string, any>,
) => performRequest({
    url: referenceUrl,
    method: requestType,
    data: convertToFormData(data),
});

export const fetchSimpleData = (entityName: string) => performRequest({
    url: `${entityName}`,
    method: ERequestType.GET,
});

export const actionRequestWithJson = (
    referenceUrl: string,
    requestType: ERequestType,
    data: Record<string, any>,
    userId?: number,
) => performRequest({
    url: `${referenceUrl}`,
    method: requestType,
    data,
    headers: {userId},
});

export type TOptionsActionRequest = {
    isJsonRequest?: boolean;
    shouldUsedFileIndexName?: boolean;
    normalizedShouldUsedFileIndexName?: boolean;
    useRawData?: boolean;
};
export const updateNameDocument = (
    referenceUrl: string,
    requestType: ERequestType,
    data: any,
) => performRequest({
    url: `${referenceUrl}`,
    method: requestType,
    params: data,
});
export const actionRequest = (
    referenceUrl: string,
    requestType: ERequestType,
    data: Record<string, any>,
    {
        isJsonRequest,
        shouldUsedFileIndexName,
        normalizedShouldUsedFileIndexName,
        useRawData,
    }: TOptionsActionRequest,
    userId: number | string | undefined,
    urlConfig?: IUrlConfig,
    headers?: Record<string, string>,
) => {
    if (isJsonRequest) {
        return performRequest({
            url: `${referenceUrl}`,
            method: requestType,
            data,
            urlConfig,
            headers,
        });
    }
    if (requestType === ERequestType.GET) {
        return performRequest({
            url: `${referenceUrl}`,
            method: requestType,
            params: data,
            urlConfig,
            headers,
        });
    }
    return performRequest({
        url: `${referenceUrl}`,
        method: requestType,
        data: useRawData
            ? data
            : convertToFormData(data, {shouldUsedFileIndexName, normalizedShouldUsedFileIndexName}),
        headers: {
            ...headers,
            userId,
        },
        urlConfig,
    });
};

export const uploadFileAdditional = (
    url: string,
    file: FormData,
    params?: any,
) => performRequest({
    method: ERequestType.POST,
    url,
    headers: {'Content-Type': 'multipart/form-data'},
    params,
    data: file,
});

export const postUploadFileAction = (
    url: string,
    data?: FormData,
    params?: any,
) => performRequest({
    url,
    method: ERequestType.POST,
    data,
    params: convertParamsForRequest(params),
});

export const fetchReferenceValue = (
    referenceUrl: string,
    id: string | number,
): TRequestPromise<TLookupValue> => performRequest({
    url: `${referenceUrl}/${id}`,
    method: ERequestType.GET,
});

export const uploadFile = (url: string, onUploadProgress: (progressEvent: any) => void) => performRequest({
    url,
    method: ERequestType.POST,
    onUploadProgress,
});

export const downloadFile = async (url: string) => {
    const fileResponse = await performRequest({
        method: ERequestType.GET,
        url,
        responseType: 'blob',
    });
    FileDownload(fileResponse.data, getFilenameFromResponse(fileResponse));
    return fileResponse;
};
